import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { userService, apprenantService } from '../services/api';
import { toast } from 'sonner';
import { Trash2, Users, User, UserPlus } from 'lucide-react';
import axios from 'axios';
import api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EditAtelierDialog = ({ atelier, open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [animateurs, setAnimateurs] = useState([]);
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [invitableUsers, setInvitableUsers] = useState([]);
  const [selectedAnimateurs, setSelectedAnimateurs] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [selectedInvites, setSelectedInvites] = useState([]);
  const [formData, setFormData] = useState({
    titre: '',
    type_atelier: 'atelier',
    date: '',
    heure_debut: '09:00',
    heure_fin: '12:00',
    zone_geographique: 'Saint-Pierre',
    lieu: '',
    description: ''
  });

  useEffect(() => {
    if (atelier && open) {
      setFormData({
        titre: atelier.titre || '',
        type_atelier: atelier.type_atelier || 'atelier',
        date: atelier.date || '',
        heure_debut: atelier.heure_debut || '09:00',
        heure_fin: atelier.heure_fin || '12:00',
        zone_geographique: atelier.zone_geographique || 'Saint-Pierre',
        lieu: atelier.lieu || '',
        description: atelier.description || ''
      });
      setSelectedAnimateurs(atelier.animateurs_ids || []);
      // Récupérer les IDs des participants existants
      const existingParticipantIds = (atelier.participants || []).map(p => p.apprenant_id || p.id);
      setSelectedParticipants(existingParticipantIds);
      // Récupérer les IDs des invités existants
      const existingInviteIds = (atelier.invites || []).map(i => i.id);
      setSelectedInvites(existingInviteIds);
      loadData();
    }
  }, [atelier, open]);

  const loadData = async () => {
    try {
      const [animateursRes, beneficiairesRes, invitablesRes] = await Promise.all([
        userService.getAnimateurs(),
        apprenantService.getAll(),
        api.get('/users/invitables')
      ]);
      setAnimateurs(animateursRes.data);
      setBeneficiaires(beneficiairesRes.data);
      setInvitableUsers(invitablesRes.data || []);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
  };

  const handleToggleAnimateur = (animateurId) => {
    setSelectedAnimateurs(prev => 
      prev.includes(animateurId) 
        ? prev.filter(id => id !== animateurId)
        : [...prev, animateurId]
    );
  };

  const handleToggleParticipant = (participantId) => {
    setSelectedParticipants(prev => 
      prev.includes(participantId) 
        ? prev.filter(id => id !== participantId)
        : [...prev, participantId]
    );
  };

  const handleSelectAllParticipants = () => {
    if (!beneficiaires || beneficiaires.length === 0) return;
    
    if (selectedParticipants.length === beneficiaires.length) {
      setSelectedParticipants([]);
    } else {
      const allIds = beneficiaires.map(b => b.id).filter(id => id); // Filter out undefined/null
      setSelectedParticipants(allIds);
    }
  };

  const handleToggleInvite = (userId) => {
    setSelectedInvites(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'financeur': return 'bg-purple-100 text-purple-700';
      case 'referent': return 'bg-green-100 text-green-700';
      case 'animateur': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getRoleLabel = (role) => {
    switch(role) {
      case 'financeur': return 'Financeur';
      case 'referent': return 'Référent';
      case 'animateur': return 'Intervenant';
      default: return role;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedAnimateurs.length === 0) {
      toast.error('Veuillez sélectionner au moins un intervenant');
      return;
    }
    
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/ateliers/${atelier.id}`, {
        ...formData,
        animateurs_ids: selectedAnimateurs,
        participants_ids: selectedParticipants,
        invites_ids: selectedInvites
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Atelier modifié avec succès');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de modification');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet atelier ?')) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/ateliers/${atelier.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Atelier supprimé');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de suppression');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Nunito, sans-serif' }}>Modifier l'atelier</DialogTitle>
          <DialogDescription>Modifiez les détails de l'atelier ou de l'événement</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input
                value={formData.titre}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={formData.type_atelier} onValueChange={(val) => setFormData({ ...formData, type_atelier: val })}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Sélectionner un type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="atelier_thematique">Atelier thématique</SelectItem>
                  <SelectItem value="job_dating_inverse">Job dating inversé</SelectItem>
                  <SelectItem value="cafe_emploi">Café de l'emploi</SelectItem>
                  <SelectItem value="visite_entreprise">Visite d'entreprise</SelectItem>
                  <SelectItem value="rencontre_metiers">Rencontre métiers en tension</SelectItem>
                  <SelectItem value="comite_suivi">Comité de suivi</SelectItem>
                  <SelectItem value="jury">Jury</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Heure début</Label>
              <Input
                type="time"
                value={formData.heure_debut}
                onChange={(e) => setFormData({ ...formData, heure_debut: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Heure fin</Label>
              <Input
                type="time"
                value={formData.heure_fin}
                onChange={(e) => setFormData({ ...formData, heure_fin: e.target.value })}
                className="rounded-xl"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Zone</Label>
            <Select value={formData.zone_geographique} onValueChange={(val) => setFormData({ ...formData, zone_geographique: val })}>
              <SelectTrigger className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Saint-Pierre">Saint-Pierre</SelectItem>
                <SelectItem value="Sainte-Marie">Sainte-Marie</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-lieu">Lieu</Label>
            <Input
              id="edit-lieu"
              value={formData.lieu}
              onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
              className="rounded-xl"
              placeholder="Ex: Salle de réunion, 12 rue des Acacias..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-description">Informations complémentaires</Label>
            <textarea
              id="edit-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
              placeholder="Programme, documents à apporter, consignes particulières..."
            />
          </div>

          {/* Sélection des intervenants */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Intervenant(s) * <span className="text-xs text-red-500">(au moins un requis)</span>
            </Label>
            <div className="border rounded-xl p-3 max-h-[120px] overflow-y-auto bg-slate-50">
              {animateurs.length === 0 ? (
                <p className="text-sm text-slate-500">Aucun intervenant disponible</p>
              ) : (
                <div className="space-y-2">
                  {animateurs.map((animateur) => (
                    <div
                      key={animateur.id}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                        selectedAnimateurs.includes(animateur.id) 
                          ? 'bg-blue-100 border border-blue-300' 
                          : 'hover:bg-slate-100'
                      }`}
                      onClick={() => handleToggleAnimateur(animateur.id)}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                        selectedAnimateurs.includes(animateur.id) 
                          ? 'bg-blue-600 border-blue-600' 
                          : 'border-slate-300'
                      }`}>
                        {selectedAnimateurs.includes(animateur.id) && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">{animateur.prenom} {animateur.nom}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedAnimateurs.length > 0 && (
              <p className="text-xs text-blue-600">{selectedAnimateurs.length} intervenant(s) sélectionné(s)</p>
            )}
          </div>

          {/* Sélection des participants */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Participants
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSelectAllParticipants}
                className="text-xs rounded-lg"
                data-testid="select-all-participants"
              >
                {selectedParticipants.length === beneficiaires.length ? 'Désélectionner tout' : 'Inscrire tout le monde'}
              </Button>
            </div>
            <div className="border rounded-xl p-3 max-h-[150px] overflow-y-auto bg-slate-50">
              {beneficiaires.length === 0 ? (
                <p className="text-sm text-slate-500">Aucun bénéficiaire disponible</p>
              ) : (
                <div className="space-y-2">
                  {beneficiaires.map((beneficiaire) => (
                    <div
                      key={beneficiaire.id}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                        selectedParticipants.includes(beneficiaire.id) 
                          ? 'bg-emerald-100 border border-emerald-300' 
                          : 'hover:bg-slate-100'
                      }`}
                      onClick={() => handleToggleParticipant(beneficiaire.id)}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                        selectedParticipants.includes(beneficiaire.id) 
                          ? 'bg-blue-600 border-blue-600' 
                          : 'border-slate-300'
                      }`}>
                        {selectedParticipants.includes(beneficiaire.id) && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">{beneficiaire.prenom} {beneficiaire.nom}</span>
                      <span className="text-xs text-slate-500">{beneficiaire.zone_geographique}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedParticipants.length > 0 && (
              <p className="text-xs text-emerald-600">{selectedParticipants.length} participant(s) sélectionné(s)</p>
            )}
          </div>

          {/* Sélection des invités — hors intervenants déjà listés dans la section Intervenant(s) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              Invités <span className="text-xs text-slate-500">(Financeurs, Référents)</span>
            </Label>
            <div className="border rounded-xl p-3 max-h-[150px] overflow-y-auto bg-slate-50">
              {invitableUsers.filter(u => !animateurs.some(a => a.id === u.id)).length === 0 ? (
                <p className="text-sm text-slate-500">Aucun utilisateur disponible</p>
              ) : (
                <div className="space-y-2">
                  {invitableUsers.filter(u => !animateurs.some(a => a.id === u.id)).map((user) => (
                    <div
                      key={user.id}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                        selectedInvites.includes(user.id) 
                          ? 'bg-purple-100 border border-purple-300' 
                          : 'hover:bg-slate-100'
                      }`}
                      onClick={() => handleToggleInvite(user.id)}
                      data-testid={`edit-invite-user-${user.id}`}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                        selectedInvites.includes(user.id) 
                          ? 'bg-purple-600 border-purple-600' 
                          : 'border-slate-300'
                      }`}>
                        {selectedInvites.includes(user.id) && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-1">
                        <span className="font-medium">{user.prenom} {user.nom}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedInvites.length > 0 && (
              <p className="text-xs text-purple-600">{selectedInvites.length} invité(s) sélectionné(s)</p>
            )}
          </div>

          <DialogFooter className="flex justify-between">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleDelete} 
              disabled={loading}
              className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">
                Annuler
              </Button>
              <Button type="submit" disabled={loading || selectedAnimateurs.length === 0} className="rounded-xl">
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditAtelierDialog;
